// OMNI Modal Component Recipe for Chakra UI v3
// Provides consistent modal styling based on omni_standard_css.css

import { defineRecipe } from '@chakra-ui/react';

// OMNI Modal Recipe
export const omniModalRecipe = defineRecipe({
  className: 'omni-modal',

  base: {
    // Modal backdrop
    '& .chakra-modal__overlay': {
      backgroundColor: 'rgba(10, 10, 10, 0.86)', // OMNI modal-background
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },

    // Modal content container
    '& .chakra-modal__content': {
      margin: { base: '0 20px', md: '0 auto' },
      width: { base: '100%', md: '640px' },
      maxHeight: { base: 'calc(100vh - 160px)', md: 'calc(100vh - 40px)' },
      overflow: 'auto',
      position: 'relative',
      borderRadius: 'var(--radius-large, 12px)',

      // OMNI modal-card styling
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'white',
      boxShadow:
        '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    },

    // Modal header
    '& .chakra-modal__header': {
      backgroundColor: 'var(--color-whitesmoke, #f5f5f5)',
      borderBottom: '1px solid var(--color-gray-db, #dbdbdb)',
      borderTopLeftRadius: 'var(--radius-large, 12px)',
      borderTopRightRadius: 'var(--radius-large, 12px)',
      padding: '20px',
      fontSize: 'var(--size-4, 1.5rem)',
      fontWeight: 'medium',
      color: 'var(--color-gray-36, #363636)',
      lineHeight: 1,
      flexShrink: 0,
    },

    // Modal body
    '& .chakra-modal__body': {
      backgroundColor: 'var(--color-white, white)',
      padding: '20px',
      flexGrow: 1,
      flexShrink: 1,
      overflow: 'auto',
      WebkitOverflowScrolling: 'touch',
    },

    // Modal footer
    '& .chakra-modal__footer': {
      backgroundColor: 'var(--color-whitesmoke, #f5f5f5)',
      borderTop: '1px solid var(--color-gray-db, #dbdbdb)',
      borderBottomLeftRadius: 'var(--radius-large, 12px)',
      borderBottomRightRadius: 'var(--radius-large, 12px)',
      padding: '20px',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: '0.5rem',
    },

    // Close button
    '& .chakra-modal__close-btn': {
      position: 'absolute',
      top: '20px',
      right: '20px',
      width: '40px',
      height: '40px',
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: '50%',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'var(--color-gray-36, #363636)',
      fontSize: '1.5rem',
      zIndex: 1,

      _hover: {
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
      },

      _focus: {
        outline: '2px solid var(--chakra-colors-brand-400)',
        outlineOffset: '2px',
      },
    },
  },

  variants: {
    // Size variants
    size: {
      sm: {
        '& .chakra-modal__content': {
          width: { base: '100%', md: '400px' },
        },
      },
      md: {
        '& .chakra-modal__content': {
          width: { base: '100%', md: '640px' },
        },
      },
      lg: {
        '& .chakra-modal__content': {
          width: { base: '100%', md: '800px' },
        },
      },
      xl: {
        '& .chakra-modal__content': {
          width: { base: '100%', md: '1000px' },
        },
      },
    },

    // Theme variants
    variant: {
      default: {
        // Uses base styling
      },
      brand: {
        '& .chakra-modal__header': {
          backgroundColor: 'brand.50',
          borderBottomColor: 'brand.200',
          color: 'brand.700',
        },
        '& .chakra-modal__footer': {
          backgroundColor: 'brand.50',
          borderTopColor: 'brand.200',
        },
      },
      neutral: {
        '& .chakra-modal__header': {
          backgroundColor: 'gray.50',
          borderBottomColor: 'gray.200',
          color: 'gray.700',
        },
        '& .chakra-modal__footer': {
          backgroundColor: 'gray.50',
          borderTopColor: 'gray.200',
        },
      },
    },
  },

  defaultVariants: {
    size: 'md',
    variant: 'default',
  },
});
